'use client';

import { isNil } from 'lodash-es';
import mergeRefs from 'merge-refs';
import { useRef, useState } from 'react';
import { SwitchTransition, Transition } from 'react-transition-group';
import { useMeasure } from 'react-use';

import { StrapiGridRevealBlock } from '@/types/blocks';
import { cx } from '@/ui/cva';

import { GridRevealBlockButton } from './GridRevealBlockButton';
import { GridRevealBlockContent } from './GridRevealBlockContent';
import { GridRevealBlockListButton } from './GridRevealBlockListButton';

export type GridRevealBlockProps = Omit<StrapiGridRevealBlock, 'config'>;

export function GridRevealBlock({
    layout,
    grid,
    ...rest
}: GridRevealBlockProps) {
    const [activeIndex, setActiveIndex] = useState<number | null>(null);

    const hasGrid = !!grid && grid?.length > 0;

    const handleClick = (index: number) => {
        setActiveIndex((prev) => (prev !== index ? index : null));
    };

    const activeItem = !isNil(activeIndex) && grid ? grid[activeIndex] : null;
    const [sizeRef, size] = useMeasure<HTMLDivElement>();
    const nodeRef = useRef<HTMLDivElement>(null);
    const ref = mergeRefs(sizeRef, nodeRef);
    const height = size.height > 0 ? size.height : undefined;

    return (
        <div className="flex flex-col lg:flex-row lg:items-center">
            <div
                className={cx(
                    'transition-all max-lg:order-1',
                    hasGrid && 'lg:basis-1/2',
                    layout === 'text|grid' && 'lg:pr-lg xl:pr-xl',
                    layout === 'grid|text' && 'lg:order-1 lg:pl-lg xl:pl-xl'
                )}
                style={{
                    height,
                }}
            >
                <SwitchTransition mode="out-in">
                    <Transition
                        key={activeItem?.id ?? 'default'}
                        nodeRef={nodeRef}
                        timeout={200}
                    >
                        {(state) => (
                            <div
                                ref={ref}
                                className={cx(
                                    'transition',
                                    state !== 'entered' && 'opacity-0',
                                    state === 'entering' && 'translate-x-lg',
                                    state === 'entered' &&
                                        'opacity-1 translate-x-0'
                                )}
                            >
                                {!activeItem ? (
                                    <GridRevealBlockContent {...rest}>
                                        {hasGrid && (
                                            <ul className="mt-md space-y-xs">
                                                {grid.map((item, index) => (
                                                    <li key={item.id}>
                                                        <GridRevealBlockListButton
                                                            onClick={() =>
                                                                handleClick(
                                                                    index
                                                                )
                                                            }
                                                        >
                                                            {item.title}
                                                        </GridRevealBlockListButton>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </GridRevealBlockContent>
                                ) : (
                                    <GridRevealBlockContent
                                        content={activeItem.content}
                                        callsToAction={activeItem.callsToAction}
                                        title={activeItem.title}
                                    />
                                )}
                            </div>
                        )}
                    </Transition>
                </SwitchTransition>
            </div>

            {hasGrid && (
                <div
                    className={cx(
                        'max-lg:order-0 mb-md lg:mb-0 lg:basis-1/2',
                        layout === 'grid|text' && 'lg:order-0'
                    )}
                >
                    <div className="mx-auto max-w-[630px]">
                        <div className="relative aspect-[3/2]">
                            <div className="absolute inset-0 size-full">
                                <div
                                    className={cx(
                                        'grid h-full w-full gap-sm',
                                        grid.length === 2 && 'grid-cols-2',
                                        grid.length === 3 && 'grid-cols-3',
                                        grid.length === 4 &&
                                            'grid-cols-2 grid-rows-2'
                                    )}
                                >
                                    {grid.map((item, index) => (
                                        <GridRevealBlockButton
                                            key={item.id}
                                            isActive={index === activeIndex}
                                            onClick={() => handleClick(index)}
                                        >
                                            {item.title}
                                        </GridRevealBlockButton>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
